import type { ApiErrorCodeI18nPatternRule } from "./error-code-i18n-pattern-types";

// Platform resource error-code i18n pattern rules.
export const API_ERROR_CODE_I18N_PLATFORM_RESOURCE_PATTERN_RULES: readonly ApiErrorCodeI18nPatternRule[] =
  [
    {
      prefix: "environment.runtime.",
      resourceI18nKey: "common.errors.apiCodeResources.environmentRuntime",
      reasonCodes: ["schema_not_ready"],
    },
    {
      prefix: "exam.attempt.",
      resourceI18nKey: "common.errors.apiCodeResources.examAttempt",
      reasonCodes: [
        "early_submit_not_allowed",
        "expired_auto_submitted",
        "max_count_exceeded",
        "not_in_progress",
        "reentry_disabled",
      ],
    },
    {
      prefix: "exam.exam.",
      resourceI18nKey: "common.errors.apiCodeResources.examExam",
      reasonCodes: [
        "candidate_not_assigned",
        "ended",
        "late_entry_disabled",
        "late_entry_window_exceeded",
        "not_open",
        "not_started",
      ],
    },
    {
      prefix: "exam.paper.",
      resourceI18nKey: "common.errors.apiCodeResources.examPaper",
      reasonCodes: [
        "actor_required",
        "archived_read_only",
        "code_required",
        "has_attempt_records",
        "invalid_status_transition",
        "items_required",
        "no_outdated_items_selected",
        "not_deletable",
        "referenced_by_exams",
        "unsupported_status_action",
      ],
    },
    {
      prefix: "exam.scoring.",
      resourceI18nKey: "common.errors.apiCodeResources.examScoring",
      reasonCodes: ["attempt_review_status_invalid"],
    },
    {
      prefix: "exam.system.",
      resourceI18nKey: "common.errors.apiCodeResources.examSystem",
      reasonCodes: [
        "integration_base_url_not_configured",
        "integration_setting_key_unknown",
        "public_asset_not_found",
        "setting_invalid",
        "setting_missing",
        "setting_payload_invalid",
        "storage_required_fields_missing",
      ],
    },
    {
      prefix: "micro_lab.library.",
      resourceI18nKey: "common.errors.apiCodeResources.microLabLibrary",
      reasonCodes: [
        "offline_requires_approved_shared_entry",
        "reuse_requires_approved_shared_entry",
        "review_decision_requires_pending_entry",
        "review_submit_owner_required",
        "review_submit_requires_draft_version",
      ],
    },
    {
      prefix: "micro_lab.release.",
      resourceI18nKey: "common.errors.apiCodeResources.microLabRelease",
      reasonCodes: [
        "archive_requires_closed",
        "close_requires_published",
        "parent_scheme_missing",
        "publish_requires_draft",
        "publish_requires_targets",
        "scheme_create_requires_draft",
        "scheme_version_create_requires_draft_or_shared",
        "target_members_invalid",
        "target_members_manage_requires_active_release",
        "target_members_manage_requires_targeted_release",
        "target_members_not_found",
        "target_members_required",
      ],
    },
    {
      prefix: "micro_lab.run.",
      resourceI18nKey: "common.errors.apiCodeResources.microLabRun",
      reasonCodes: [
        "internal_callback_token_invalid",
        "internal_callback_token_missing",
        "reset_requires_latest_attempt",
        "reset_requires_started_attempt",
      ],
    },
    {
      prefix: "micro_lab.scheme.",
      resourceI18nKey: "common.errors.apiCodeResources.microLabScheme",
      reasonCodes: ["share_public_requires_review_pipeline"],
    },
    {
      prefix: "redblue.activity.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueActivity",
      reasonCodes: [
        "duplicate_member_user_id",
        "duplicate_role_for_member",
        "duplicate_seat_binding",
        "execution_member_observer_conflict",
        "members_inactive",
        "members_missing",
        "members_not_in_directory",
        "non_execution_member_seat_binding",
        "non_execution_role_camp_binding",
        "prepare_projection_invalid",
        "prepare_request_mismatch",
        "release_projection_invalid",
        "release_request_mismatch",
        "release_topology_instance_mismatch",
        "schedule_not_ready",
        "schedule_window_invalid",
        "scheme_version_mismatch",
        "scheme_version_not_found",
        "scheme_version_not_published",
        "seats_not_fully_bound",
        "snapshot_design_incomplete",
        "snapshot_missing",
        "staff_execution_role_conflict",
        "status_transition_invalid",
        "terminal_readonly",
        "topology_binding_incomplete",
        "topology_binding_not_found",
        "topology_evidence_invalid",
        "topology_instance_id_missing",
        "topology_not_prepared",
        "unknown_camp_code",
        "unknown_seat_code",
      ],
    },
    {
      prefix: "redblue.director.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueDirector",
      reasonCodes: ["assigned_by_required", "published_by_required"],
    },
    {
      prefix: "redblue.director.activity_task.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueDirectorActivityTask",
      reasonCodes: ["code_already_exists"],
    },
    {
      prefix: "redblue.director.activity_tasks.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueDirectorActivityTasks",
      reasonCodes: ["not_pending_dispatch"],
    },
    {
      prefix: "redblue.director.task_assignment.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueDirectorTaskAssignment",
      reasonCodes: [
        "already_assigned_to_target_seat",
        "requires_dispatched_task",
        "scope_forbidden",
      ],
    },
    {
      prefix: "redblue.director.task_codes.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueDirectorTaskCodes",
      reasonCodes: ["duplicate"],
    },
    {
      prefix: "redblue.environment.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueEnvironment",
      reasonCodes: ["template_version_mismatch", "topology_version_not_published"],
    },
    {
      prefix: "redblue.intelligence.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueIntelligence",
      reasonCodes: [
        "access_token_missing",
        "apply_requires_draft",
        "generation_failed",
        "generation_not_deletable",
        "generation_not_found",
        "generation_not_reviewable",
        "integration_not_configured",
        "invalid_payload",
        "parent_generation_invalid",
        "request_failed",
        "upstream_rejected",
      ],
    },
    {
      prefix: "redblue.judge.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueJudge",
      reasonCodes: [
        "activity_not_adjustable",
        "activity_not_reviewable",
        "activity_not_scoreable",
        "confirmed_by_required",
        "decided_by_required",
      ],
    },
    {
      prefix: "redblue.judge.final_score.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueJudgeFinalScore",
      reasonCodes: ["requires_ended_status"],
    },
    {
      prefix: "redblue.judge.score_record.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueJudgeScoreRecord",
      reasonCodes: ["not_pending"],
    },
    {
      prefix: "redblue.observer.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueObserver",
      reasonCodes: ["activity_not_observable"],
    },
    {
      prefix: "redblue.participant.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueParticipant",
      reasonCodes: [
        "flag_completion_condition_missing",
        "flag_task_requires_flags_endpoint",
        "task_not_flag",
        "tasks_not_visible",
        "workspace_not_visible",
        "write_requires_running_activity",
      ],
    },
    {
      prefix: "redblue.participant.activity_task.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueParticipantActivityTask",
      reasonCodes: ["not_visible_to_member"],
    },
    {
      prefix: "redblue.participant.report.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueParticipantReport",
      reasonCodes: ["not_draft", "not_owned_by_member"],
    },
    {
      prefix: "redblue.scheme_design.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueSchemeDesign",
      reasonCodes: ["duplicate_code", "unknown_reference"],
    },
    {
      prefix: "redblue.scheme_topology_binding.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueSchemeTopologyBinding",
      reasonCodes: ["incomplete", "locked", "required_for_version_create"],
    },
    {
      prefix: "redblue.scheme_topology_sync.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueSchemeTopologySync",
      reasonCodes: ["binding_missing", "role_unmapped", "roles_missing"],
    },
    {
      prefix: "redblue.scheme_version.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueSchemeVersion",
      reasonCodes: ["activity_create_not_ready", "readonly_published"],
    },
    {
      prefix: "redblue.scheme.",
      resourceI18nKey: "common.errors.apiCodeResources.redblueScheme",
      reasonCodes: ["all_scope_requires_admin", "archived_readonly", "share_forbidden"],
    },
    {
      prefix: "platform.admin.auth.",
      resourceI18nKey: "common.errors.apiCodeResources.platformAdminAuth",
      reasonCodes: ["invalid_app_session"],
    },
    {
      prefix: "platform.app.",
      resourceI18nKey: "common.errors.apiCodeResources.platformApp",
      reasonCodes: ["unsupported_app_key"],
    },
    {
      prefix: "platform.asset.",
      resourceI18nKey: "common.errors.apiCodeResources.platformAsset",
      reasonCodes: ["reference_contract_invalid"],
    },
    {
      prefix: "platform.asset_center.asset.",
      resourceI18nKey: "common.errors.apiCodeResources.platformAssetCenterAsset",
      reasonCodes: ["delete_blocked", "delete_in_use"],
    },
    {
      prefix: "platform.asset_center.auth.",
      resourceI18nKey: "common.errors.apiCodeResources.platformAssetCenterAuth",
      reasonCodes: ["invalid_app_session"],
    },
    {
      prefix: "platform.asset_center.object_space.",
      resourceI18nKey: "common.errors.apiCodeResources.platformAssetCenterObjectSpace",
      reasonCodes: [
        "access_level_invalid",
        "grant_already_exists",
        "grant_not_found",
        "grant_update_payload_required",
        "grantee_type_invalid",
        "grantee_value_required",
        "key_required",
        "not_found",
        "not_readable",
        "not_writable",
        "policy_already_exists",
        "policy_not_found",
        "policy_update_payload_required",
        "s3_temp_credentials_audit_persistence_failed",
        "s3_temp_credentials_request_failed",
        "s3_temp_credentials_upstream_invalid_response",
        "scope_type_invalid",
        "scope_value_invalid",
        "status_invalid",
        "storage_mapping_incomplete",
        "temp_credentials_backend_invalid",
        "write_policy_invalid",
      ],
    },
    {
      prefix: "platform.asset_center.reference.",
      resourceI18nKey: "common.errors.apiCodeResources.platformAssetCenterReference",
      reasonCodes: [
        "filter_invalid",
        "resource_type_required",
        "scope_required",
        "service_required",
      ],
    },
    {
      prefix: "platform.asset_center.upload.",
      resourceI18nKey: "common.errors.apiCodeResources.platformAssetCenterUpload",
      reasonCodes: ["not_finished"],
    },
    {
      prefix: "platform.business_reference.auth.",
      resourceI18nKey: "common.errors.apiCodeResources.platformBusinessReferenceAuth",
      reasonCodes: ["invalid_app_session"],
    },
    {
      prefix: "platform.business_reference.request.",
      resourceI18nKey: "common.errors.apiCodeResources.platformBusinessReferenceRequest",
      reasonCodes: ["duplicate_source", "invalid_format"],
    },
    {
      prefix: "platform.capability_graph.auth.",
      resourceI18nKey: "common.errors.apiCodeResources.platformCapabilityGraphAuth",
      reasonCodes: ["invalid_app_session", "invalid_internal_token"],
    },
    {
      prefix: "platform.capability_graph.",
      resourceI18nKey: "common.errors.apiCodeResources.platformCapabilityGraph",
      reasonCodes: [
        "access_forbidden",
        "integration_not_configured",
        "invalid_payload",
        "request.invalid_format",
        "unavailable",
      ],
    },
    {
      prefix: "platform.dependency.",
      resourceI18nKey: "common.errors.apiCodeResources.platformDependency",
      reasonCodes: ["database_not_ready"],
    },
    {
      prefix: "platform.execution_fabric.auth.",
      resourceI18nKey: "common.errors.apiCodeResources.platformExecutionFabricAuth",
      reasonCodes: [
        "execution_admin_required",
        "invalid_app_session",
        "sso_integration_disabled",
        "sso_unavailable",
      ],
    },
    {
      prefix: "platform.execution_fabric.endpoint.",
      resourceI18nKey: "common.errors.apiCodeResources.platformExecutionFabricEndpoint",
      reasonCodes: ["disabled"],
    },
    {
      prefix: "platform.execution_fabric.environment_instance.",
      resourceI18nKey: "common.errors.apiCodeResources.platformExecutionFabricEnvironmentInstance",
      reasonCodes: ["access_denied", "binding_invalid"],
    },
    {
      prefix: "platform.execution_fabric.orchestration_request.",
      resourceI18nKey: "common.errors.apiCodeResources.platformExecutionFabricOrchestrationRequest",
      reasonCodes: ["contract_invalid"],
    },
    {
      prefix: "platform.execution_fabric.remote_session.",
      resourceI18nKey: "common.errors.apiCodeResources.platformExecutionFabricRemoteSession",
      reasonCodes: ["access_denied"],
    },
    {
      prefix: "platform.execution_fabric.request.",
      resourceI18nKey: "common.errors.apiCodeResources.platformExecutionFabricRequest",
      reasonCodes: ["invalid", "request_id_missing"],
    },
    {
      prefix: "platform.execution_fabric.runtime_registry.",
      resourceI18nKey: "common.errors.apiCodeResources.platformExecutionFabricRuntimeRegistry",
      reasonCodes: ["invalid_token", "worker_identity_invalid"],
    },
    {
      prefix: "platform.execution_fabric.task_result.",
      resourceI18nKey: "common.errors.apiCodeResources.platformExecutionFabricTaskResult",
      reasonCodes: ["missing"],
    },
    {
      prefix: "platform.execution_fabric.topology_snapshot.",
      resourceI18nKey: "common.errors.apiCodeResources.platformExecutionFabricTopologySnapshot",
      reasonCodes: ["contract_invalid"],
    },
    {
      prefix: "platform.execution_fabric.upstream.",
      resourceI18nKey: "common.errors.apiCodeResources.platformExecutionFabricUpstream",
      reasonCodes: ["invalid_response"],
    },
    {
      prefix: "platform.identity.application.",
      resourceI18nKey: "common.errors.apiCodeResources.platformIdentityApplication",
      reasonCodes: ["not_found"],
    },
    {
      prefix: "platform.identity.auth.",
      resourceI18nKey: "common.errors.apiCodeResources.platformIdentityAuth",
      reasonCodes: ["identity_admin_required", "invalid_internal_lookup_secret"],
    },
    {
      prefix: "platform.identity.dependency.",
      resourceI18nKey: "common.errors.apiCodeResources.platformIdentityDependency",
      reasonCodes: ["datastore_unavailable"],
    },
    {
      prefix: "platform.identity.directory_picker.",
      resourceI18nKey: "common.errors.apiCodeResources.platformIdentityDirectoryPicker",
      reasonCodes: ["data_invalid", "scope_invalid", "scope_not_found", "tree_invalid"],
    },
    {
      prefix: "platform.identity.internal.",
      resourceI18nKey: "common.errors.apiCodeResources.platformIdentityInternal",
      reasonCodes: ["error"],
    },
    {
      prefix: "platform.identity.organization.",
      resourceI18nKey: "common.errors.apiCodeResources.platformIdentityOrganization",
      reasonCodes: ["not_found"],
    },
    {
      prefix: "platform.identity.platform_overview.",
      resourceI18nKey: "common.errors.apiCodeResources.platformIdentityPlatformOverview",
      reasonCodes: ["asset_remediation_unavailable"],
    },
    {
      prefix: "platform.identity.request.",
      resourceI18nKey: "common.errors.apiCodeResources.platformIdentityRequest",
      reasonCodes: ["ids_required", "invalid"],
    },
    {
      prefix: "platform.identity.resource.",
      resourceI18nKey: "common.errors.apiCodeResources.platformIdentityResource",
      reasonCodes: ["not_found"],
    },
    {
      prefix: "platform.identity.user.",
      resourceI18nKey: "common.errors.apiCodeResources.platformIdentityUser",
      reasonCodes: [
        "current_user_disable_forbidden",
        "current_user_kickout_forbidden",
        "current_user_password_reset_forbidden",
        "expired",
        "nested_impersonation_forbidden",
        "not_active_or_not_found",
        "not_found",
      ],
    },
    {
      prefix: "platform.identity.",
      resourceI18nKey: "common.errors.apiCodeResources.platformIdentity",
      reasonCodes: [
        "access_forbidden",
        "invalid_payload",
        "invalid_user_payload",
        "sso_unavailable",
      ],
    },
    {
      prefix: "platform.knowledge_library.auth.",
      resourceI18nKey: "common.errors.apiCodeResources.platformKnowledgeLibraryAuth",
      reasonCodes: [
        "actor_user_id_required",
        "admin_required",
        "internal_actor_user_id_required",
        "invalid_internal_token",
        "invalid_sso_user_payload",
        "invalid_token",
        "missing_bearer_token",
        "sso_integration_disabled",
        "sso_unavailable",
      ],
    },
    {
      prefix: "platform.knowledge_library.category.",
      resourceI18nKey: "common.errors.apiCodeResources.platformKnowledgeLibraryCategory",
      reasonCodes: ["archived", "duplicate_name", "not_found"],
    },
    {
      prefix: "platform.knowledge_library.item.",
      resourceI18nKey: "common.errors.apiCodeResources.platformKnowledgeLibraryItem",
      reasonCodes: [
        "access_forbidden",
        "archived_read_only",
        "not_found",
        "owner_forbidden",
        "scope_forbidden",
        "share_contract_invalid",
      ],
    },
    {
      prefix: "platform.knowledge_library.knowledge_code.",
      resourceI18nKey: "common.errors.apiCodeResources.platformKnowledgeLibraryKnowledgeCode",
      reasonCodes: ["allocation_conflict", "allocation_invalid", "sequence_exhausted"],
    },
    {
      prefix: "platform.knowledge_library.request.",
      resourceI18nKey: "common.errors.apiCodeResources.platformKnowledgeLibraryRequest",
      reasonCodes: ["invalid_choice", "required_field"],
    },
    {
      prefix: "platform.ops_center.logs.",
      resourceI18nKey: "common.errors.apiCodeResources.platformOpsCenterLogs",
      reasonCodes: ["loki_invalid_response", "loki_not_configured", "loki_unavailable"],
    },
    {
      prefix: "platform.tactics_techniques_hub.auth.",
      resourceI18nKey: "common.errors.apiCodeResources.platformTacticsTechniquesHubAuth",
      reasonCodes: [
        "admin_required",
        "invalid_app_session",
        "invalid_sso_user_payload",
        "invalid_token",
        "missing_bearer_token",
        "sso_integration_disabled",
        "sso_unavailable",
      ],
    },
    {
      prefix: "platform.tactics_techniques_hub.binding.",
      resourceI18nKey: "common.errors.apiCodeResources.platformTacticsTechniquesHubBinding",
      reasonCodes: ["not_found"],
    },
    {
      prefix: "platform.tactics_techniques_hub.framework.",
      resourceI18nKey: "common.errors.apiCodeResources.platformTacticsTechniquesHubFramework",
      reasonCodes: ["not_found"],
    },
    {
      prefix: "platform.tactics_techniques_hub.framework_node.",
      resourceI18nKey: "common.errors.apiCodeResources.platformTacticsTechniquesHubFrameworkNode",
      reasonCodes: ["not_found", "not_in_framework"],
    },
    {
      prefix: "platform.tactics_techniques_hub.framework_version.",
      resourceI18nKey:
        "common.errors.apiCodeResources.platformTacticsTechniquesHubFrameworkVersion",
      reasonCodes: [
        "mapping_versions_must_differ",
        "not_activatable",
        "not_found",
        "not_in_framework",
      ],
    },
    {
      prefix: "platform.tools_library.asset_download.",
      resourceI18nKey: "common.errors.apiCodeResources.platformToolsLibraryAssetDownload",
      reasonCodes: ["failed"],
    },
    {
      prefix: "platform.tools_library.asset_upload.",
      resourceI18nKey: "common.errors.apiCodeResources.platformToolsLibraryAssetUpload",
      reasonCodes: ["configuration_missing", "failed", "missing_asset_reference"],
    },
    {
      prefix: "platform.tools_library.auth.",
      resourceI18nKey: "common.errors.apiCodeResources.platformToolsLibraryAuth",
      reasonCodes: [
        "admin_required",
        "internal_actor_user_id_required",
        "invalid_app_session",
        "invalid_internal_token",
        "invalid_sso_user_payload",
        "invalid_token",
        "missing_bearer_token",
        "operator_id_required_for_asset_upload",
        "sso_integration_disabled",
        "sso_unavailable",
      ],
    },
    {
      prefix: "platform.tools_library.category.",
      resourceI18nKey: "common.errors.apiCodeResources.platformToolsLibraryCategory",
      reasonCodes: ["archived", "duplicate_name", "has_published_tools", "not_found"],
    },
    {
      prefix: "platform.tools_library.request.",
      resourceI18nKey: "common.errors.apiCodeResources.platformToolsLibraryRequest",
      reasonCodes: ["invalid_choice", "required_field"],
    },
    {
      prefix: "platform.tools_library.tool.",
      resourceI18nKey: "common.errors.apiCodeResources.platformToolsLibraryTool",
      reasonCodes: ["archived", "duplicate_code", "file_required", "file_too_large", "not_found"],
    },
    {
      prefix: "platform.tools_library.tool_code.",
      resourceI18nKey: "common.errors.apiCodeResources.platformToolsLibraryToolCode",
      reasonCodes: ["allocation_invalid", "sequence_exhausted"],
    },
    {
      prefix: "platform.traffic_library.auth.",
      resourceI18nKey: "common.errors.apiCodeResources.platformTrafficLibraryAuth",
      reasonCodes: [
        "actor_user_id_required",
        "admin_required",
        "internal_actor_user_id_required",
        "invalid_app_session",
        "invalid_internal_token",
        "invalid_sso_user_payload",
        "invalid_token",
        "missing_bearer_token",
        "sso_integration_disabled",
        "sso_unavailable",
      ],
    },
    {
      prefix: "platform.traffic_library.packet_category.",
      resourceI18nKey: "common.errors.apiCodeResources.platformTrafficLibraryPacketCategory",
      reasonCodes: ["archived", "duplicate_name", "not_found"],
    },
    {
      prefix: "platform.traffic_library.packet_file.",
      resourceI18nKey: "common.errors.apiCodeResources.platformTrafficLibraryPacketFile",
      reasonCodes: [
        "access_forbidden",
        "archived_read_only",
        "asset_download_failed",
        "not_found",
        "owner_forbidden",
        "preflight_failed",
        "scope_forbidden",
        "share_contract_invalid",
      ],
    },
    {
      prefix: "platform.traffic_library.request.",
      resourceI18nKey: "common.errors.apiCodeResources.platformTrafficLibraryRequest",
      reasonCodes: ["invalid_choice", "required_field"],
    },
    {
      prefix: "platform.traffic_library.traffic_code.",
      resourceI18nKey: "common.errors.apiCodeResources.platformTrafficLibraryTrafficCode",
      reasonCodes: ["allocation_conflict", "allocation_invalid", "sequence_exhausted"],
    },
  ];
